import React, { useState, useEffect, useCallback } from 'react';
import axios from '../utils/api';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TemplateContainer } from '../types/template';
import ContainerEditor from './ContainerEditor';
import ContainerCreationModal from './ContainerCreationModal';
import './HtmlTemplateBuilder.css';

type JsonObject = Record<string, unknown>;

type VisualStyle = {
  icon?: string;
  borderColor?: string;
  [key: string]: unknown;
};

type TemplateValidationResults = {
  error_count: number;
  warning_count: number;
  [key: string]: unknown;
};

type AxiosErrorLike = {
  response?: {
    data?: {
      error?: string;
    };
  };
};

export function getApiErrorMessage(error: unknown, fallback: string): string {
  const maybe = error as AxiosErrorLike;
  return maybe?.response?.data?.error || fallback;
}

type TemplateContainerRow = Omit<TemplateContainer, 'template_id' | 'column_id' | 'parent_container_id'> & {
  template_id?: number | null;
  column_id?: number | null;
  parent_container_id?: number | null;
};

// Типы для TypeScript
interface HtmlTemplate {
  id: number;
  name: string;
  description: string;
  layout_type: string;
  status: string;
  columns: TemplateColumn[];
  containers: TemplateContainerRow[];
  metadata: JsonObject;
  component_opacity?: number | null; // Прозрачность компонентов для этого шаблона
}

interface TemplateColumn {
  id: number;
  column_name: string;
  column_type: 'main' | 'sidebar';
  width_value: string;
  min_width?: string;
  max_width?: string;
  position: number;
  content_type: 'static' | 'dynamic';
  responsive_behavior?: JsonObject;
}


interface DynamicContainer {
  id: number;
  name: string;
  description?: string;
  container_type: string;
  placement: 'main-only' | 'sidebar-only' | 'any' | 'top-bottom';
  min_components: number;
  max_components: number;
  visual_style: VisualStyle;
  is_system: boolean;
}

type HtmlTemplateSavePayload = {
  id?: number;
  name: string;
  description: string;
  layout_type: string;
  metadata: JsonObject;
  status: string;
  columns: TemplateColumn[];
  containers: TemplateContainerRow[];
  component_opacity?: number | null; // Прозрачность компонентов для этого шаблона
};

// Типы контейнеров для drag-and-drop
const ItemTypes = {
  CONTAINER: 'container',
  DYNAMIC_CONTAINER: 'dynamic_container'
} as const;

type DragItemType = typeof ItemTypes[keyof typeof ItemTypes];
type OutsidePlacement = 'top' | 'bottom';

type LibraryContainerItem = {
  id?: number;
  type?: string;
  name: string;
  description?: string;
  icon?: string;
  maxCount?: number | null;
  container_type?: string;
  placement?: string;
  placement_type?: string;
  visual_style?: VisualStyle;
  min_components?: number;
  max_components?: number;
};

type DragDropItem = LibraryContainerItem & {
  dragType: DragItemType;
};

// Системные контейнеры
const SYSTEM_CONTAINERS = [
  {
    type: 'header',
    name: 'Хедер (Шапка)',
    icon: '🔝',
    maxCount: 1,
    placement: 'top',
    description: 'Верхняя часть сайта с логотипом и навигацией'
  },
  {
    type: 'footer',
    name: 'Футер (Подвал)',
    icon: '🔻',
    maxCount: 1,
    placement: 'bottom',
    description: 'Нижняя часть сайта с копирайтом и ссылками'
  },
  {
    type: 'menu',
    name: 'Меню',
    icon: '☰',
    maxCount: null,
    placement: 'any',
    description: 'Навигационное меню'
  },
  {
    type: 'custom',
    name: 'Пользовательский блок',
    icon: '📦',
    maxCount: null,
    placement: 'any',
    description: 'Настраиваемый контейнер для любого контента: баннеры, формы, текстовые блоки, слайдеры и др.'
  }
];

// Компонент зоны размещения вне колонок (header/footer)
const OutsideDropZone: React.FC<{
  placement: OutsidePlacement;
  containers: TemplateContainerRow[];
  onDrop: (item: DragDropItem, placement: OutsidePlacement) => void;
  onRemoveContainer: (containerId: number) => void;
  onUpdateContainers?: (containers: TemplateContainerRow[]) => void;
}> = ({ placement, containers, onDrop, onRemoveContainer, onUpdateContainers }) => {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: [ItemTypes.CONTAINER, ItemTypes.DYNAMIC_CONTAINER],
    drop: (item: DragDropItem) => {
      // Проверяем, что контейнер может быть размещен в этой зоне
      const itemPlacement = item.placement_type || item.placement || item.container_type;
      
      console.log('🎲 OutsideDropZone drop:', { 
        placement, 
        itemPlacement, 
        item_name: item.name,
        item_placement_type: item.placement_type,
        item_container_type: item.container_type 
      });
      
      if (placement === 'top' && (itemPlacement === 'header' || itemPlacement === 'menu' || itemPlacement === 'any')) {
        console.log('✅ Вызываем onDrop для top/header');
        onDrop(item, placement);
      } else if (placement === 'bottom' && (itemPlacement === 'footer' || itemPlacement === 'menu' || itemPlacement === 'any')) {
        console.log('✅ Вызываем onDrop для bottom/footer');
        onDrop(item, placement);
      } else if (itemPlacement === 'any') {
        console.log('✅ Вызываем onDrop для any');
        onDrop(item, placement);
      } else {
        console.log('❌ Контейнер не может быть размещен в этой зоне');
      }
    },
    canDrop: (item: DragDropItem) => {
      // Проверяем placement_type для контейнеров из библиотеки
      const itemPlacement = item.placement_type || item.placement || item.container_type;
      
      // Разрешаем размещение если:
      // - placement_type совпадает с зоной размещения
      // - placement_type = 'any' 
      // - для header зоны разрешаем header и menu контейнеры
      // - для footer зоны разрешаем footer и menu контейнеры
      if (placement === 'top') {
        return itemPlacement === 'header' || itemPlacement === 'menu' || itemPlacement === 'any';
      } else if (placement === 'bottom') {
        return itemPlacement === 'footer' || itemPlacement === 'menu' || itemPlacement === 'any';
      }
      return itemPlacement === 'any';
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  });

  const zoneContainers = containers
    .filter(c => {
      // Проверяем по placement_type или settings.placement
      const containerPlacement = c.placement_type || c.settings?.placement;
      
      // Для top зоны показываем контейнеры с placement_type = 'header'
      // Для bottom зоны показываем контейнеры с placement_type = 'footer'
      if (placement === 'top') {
        return !c.parent_container_id && (containerPlacement === 'header' || c.settings?.placement === 'top' || c.position < -900);
      } else if (placement === 'bottom') {
        return !c.parent_container_id && (containerPlacement === 'footer' || c.settings?.placement === 'bottom' || c.position > 900);
      }
      return false;
    })
    .sort((a, b) => a.position - b.position); // Сортируем по position

  const handleMoveContainer = (containerId: number, direction: 'up' | 'down') => {
    const currentIndex = zoneContainers.findIndex(c => c.id === containerId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= zoneContainers.length) return;
    
    // Обновляем position для двух контейнеров
    const container1 = zoneContainers[currentIndex];
    const container2 = zoneContainers[newIndex];
    
    const tempPosition = container1.position;
    container1.position = container2.position;
    container2.position = tempPosition;
    
    // Обновляем состояние
    const updatedContainers = containers.map(c => {
      if (c.id === container1.id) return container1;
      if (c.id === container2.id) return container2;
      return c;
    });
    
    // Обновляем через callback (нужно добавить в props)
    if (onUpdateContainers) {
      onUpdateContainers(updatedContainers);
    }
  };

  return (
    <div 
      ref={(node) => { drop(node); }}
      className={`template-builder__outside-zone template-builder__outside-zone--${placement} ${
        isOver && canDrop ? 'template-builder__outside-zone--drag-over' : ''
      } ${canDrop ? 'template-builder__outside-zone--can-drop' : ''}`}
    >
      <div className="template-builder__zone-label">
        {placement === 'top' ? '🔝 Верхняя область (Header/Menu)' : '🔻 Нижняя область (Footer/Menu)'}
        {zoneContainers.length > 0 && (
          <span className="template-builder__zone-count">
            ({zoneContainers.length} элем.)
          </span>
        )}
      </div>
      
      {zoneContainers.length === 0 ? (
        <div className="template-builder__zone-placeholder">
          Перетащите сюда контейнеры типа {placement === 'top' ? 'Header или Menu' : 'Footer или Menu'}
        </div>
      ) : (
        <div className="template-builder__zone-containers">
          {zoneContainers.map((container, index) => (
            <div key={container.id} className="template-builder__outside-container">
              <div className="template-builder__container-header">
                <span className="template-builder__container-order">
                  #{index + 1}
                </span>
                <span className="template-builder__container-icon">
                  {container.container_type === 'header' ? '🔝' : 
                   container.container_type === 'footer' ? '🔻' : '☰'}
                </span>
                <span className="template-builder__container-name">{container.name}</span>
                
                <div className="template-builder__container-controls">
                  <button 
                    className="template-builder__move-btn template-builder__move-btn--up"
                    onClick={() => container.id && handleMoveContainer(container.id, 'up')}
                    disabled={index === 0}
                    title="Переместить вверх"
                  >
                    ▲
                  </button>
                  <button 
                    className="template-builder__move-btn template-builder__move-btn--down"
                    onClick={() => container.id && handleMoveContainer(container.id, 'down')}
                    disabled={index === zoneContainers.length - 1}
                    title="Переместить вниз"
                  >
                    ▼
                  </button>
                  <button 
                    className="template-builder__remove-btn"
                    onClick={() => container.id && onRemoveContainer(container.id)}
                    title="Удалить контейнер"
                  >
                    ✕
                  </button>
                </div>
              </div>
              {container.description && (
                <div className="template-builder__container-description">
                  {container.description}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Компонент перетаскиваемого контейнера из библиотеки
const DraggableContainer: React.FC<{
  container: LibraryContainerItem;
  type: DragItemType;
}> = ({ container, type }) => {
  const [{ isDragging }, drag] = useDrag({
    type: type,
    item: { ...container, dragType: type },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  const getIcon = () => {
    if (type === ItemTypes.CONTAINER) {
      return container.icon || '📦';
    } else {
      const style = container.visual_style || {};
      return style.icon || '🎯';
    }
  };

  const getBorderColor = () => {
    if (type === ItemTypes.DYNAMIC_CONTAINER) {
      return container.visual_style?.borderColor || '#ccc';
    }
    return '#007bff';
  };

  return (
    <div
      ref={(node) => { drag(node); }}
      className={`template-builder__library-item ${isDragging ? 'dragging' : ''}`}
      style={{
        borderLeft: `4px solid ${getBorderColor()}`,
        opacity: isDragging ? 0.5 : 1
      }}
    >
      <span className="template-builder__library-item-icon">{getIcon()}</span>
      <div className="template-builder__library-item-content">
        <div className="template-builder__library-item-name">{container.name}</div>
        {container.description && (
          <div className="template-builder__library-item-description">
            {container.description}
          </div>
        )}
        {type === ItemTypes.DYNAMIC_CONTAINER && (
          <div className="template-builder__library-item-meta">
            <span className="badge">{container.placement}</span>
            <span className="badge">{container.min_components}-{container.max_components} компонентов</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Компонент колонки с drop зоной
const TemplateColumnComponent: React.FC<{
  column: TemplateColumn;
  containers: TemplateContainerRow[];
  onDrop: (item: DragDropItem, columnId: number) => void;
  onRemoveContainer: (containerId: number) => void;
  onEditContainer: (container: TemplateContainerRow) => void;
  onUpdateContainers?: (containers: TemplateContainerRow[]) => void;
  onUpdateColumn?: (column: TemplateColumn) => void;
}> = ({ column, containers, onDrop, onRemoveContainer, onEditContainer, onUpdateContainers, onUpdateColumn }) => {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: [ItemTypes.CONTAINER, ItemTypes.DYNAMIC_CONTAINER],
    drop: (item: DragDropItem) => onDrop(item, column.id),
    canDrop: (item: DragDropItem) => {
      // Валидация размещения динамических контейнеров
      if (item.dragType === ItemTypes.DYNAMIC_CONTAINER) {
        if (item.placement === 'main-only' && column.column_type !== 'main') {
          return false;
        }
        if (item.placement === 'sidebar-only' && column.column_type !== 'sidebar') {
          return false;
        }
      }
      return true;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  });

  const getDropzoneClass = () => {
    if (isOver && canDrop) return 'can-drop';
    if (isOver && !canDrop) return 'cannot-drop';
    return '';
  };

  // Функция изменения порядка контейнеров в колонке
  const handleMoveContainer = (containerId: number, direction: 'up' | 'down') => {
    const columnContainers = containers
      .filter(c => c.column_id === column.id)
      .sort((a, b) => a.position - b.position);
    
    const currentIndex = columnContainers.findIndex(c => c.id === containerId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= columnContainers.length) return;
    
    // Обновляем position для двух контейнеров
    const container1 = columnContainers[currentIndex];
    const container2 = columnContainers[newIndex];
    
    const tempPosition = container1.position;
    container1.position = container2.position;
    container2.position = tempPosition;
    
    // Обновляем состояние
    const updatedContainers = containers.map(c => {
      if (c.id === container1.id) return container1;
      if (c.id === container2.id) return container2;
      return c;
    });
    
    if (onUpdateContainers) {
      onUpdateContainers(updatedContainers);
    }
  };

  return (
    <div
      ref={(node) => { drop(node); }}
      className={`template-builder__column ${getDropzoneClass()}`}
      style={{ width: column.width_value }}
    >
      <div className="template-builder__column-header">
        <span className="template-builder__column-name">{column.column_name}</span>
        <span className={`template-builder__column-type ${column.column_type}`}>
          {column.column_type === 'main' ? 'Основная' : 'Боковая'}
        </span>
        <div className="template-builder__column-width-editor">
          <input
            type="text"
            value={column.width_value}
            onChange={(e) => {
              if (onUpdateColumn) {
                const updatedColumn = { ...column, width_value: e.target.value };
                onUpdateColumn(updatedColumn);
              }
            }}
            className="template-builder__width-input"
            placeholder="100%"
            title="Ширина колонки (например: 50%, 300px)"
          />
        </div>
      </div>
      
      <div className="template-builder__column-content">
        {containers
          .filter(c => c.column_id === column.id)
          .sort((a, b) => a.position - b.position)
          .map((container, index, columnContainers) => (
            <div
              key={container.id}
              className="template-builder__container"
              style={{
                borderColor: container.visual_style?.borderColor || '#007bff'
              }}
            >
              <div className="template-builder__container-header">
                <span className="template-builder__container-order">
                  #{index + 1}
                </span>
                <span className="template-builder__container-name">{container.name}</span>
                <span className="template-builder__container-type">{container.container_type}</span>
                <div className="template-builder__container-actions">
                  <button
                    type="button"
                    className="template-builder__move-btn template-builder__move-btn--up"
                    onClick={() => container.id && handleMoveContainer(container.id, 'up')}
                    disabled={index === 0}
                    title="Переместить вверх"
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    className="template-builder__move-btn template-builder__move-btn--down"
                    onClick={() => container.id && handleMoveContainer(container.id, 'down')}
                    disabled={index === columnContainers.length - 1}
                    title="Переместить вниз"
                  >
                    ▼
                  </button>
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={() => onEditContainer(container)}
                    title="Редактировать"
                  >
                    ✏️
                  </button>
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={() => container.id && onRemoveContainer(container.id)}
                    title="Удалить"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              {container.description && (
                <div className="template-builder__container-description">
                  {container.description}
                </div>
              )}
              {container.container_type === 'dynamic' && (
                <div className="template-builder__container-meta">
                  <span>Компонентов: {container.min_components}-{container.max_components}</span>
                  {container.placement_type && (
                    <span>Размещение: {container.placement_type}</span>
                  )}
                </div>
              )}
            </div>
          ))}
        
        {containers.filter(c => c.column_id === column.id).length === 0 && (
          <div className="template-builder__column-dropzone">
            <span>Перетащите контейнеры сюда</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Основной компонент конструктора
const HtmlTemplateBuilder: React.FC<{
  templateId?: number;
  onSave?: (template: HtmlTemplate) => void;
  onCancel?: () => void;
}> = ({ templateId, onSave, onCancel }) => {
  const [template, setTemplate] = useState<HtmlTemplate | null>(null);
  const [dynamicContainers, setDynamicContainers] = useState<DynamicContainer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [editingContainer, setEditingContainer] = useState<TemplateContainerRow | null>(null);
  const [showContainerEditor, setShowContainerEditor] = useState(false);
  const [showContainerCreation, setShowContainerCreation] = useState(false);
  const [showCreateDynamic, setShowCreateDynamic] = useState(false);
  const [newDynamicName, setNewDynamicName] = useState('');
  const [newDynamicDescription, setNewDynamicDescription] = useState('');
  const [newDynamicPlacement, setNewDynamicPlacement] = useState<DynamicContainer['placement']>('any');
  const [validationResults, setValidationResults] = useState<TemplateValidationResults | null>(null);

  // Загрузка шаблона при монтировании
  useEffect(() => {
    if (templateId) {
      loadTemplate();
    } else {
      // Создаем новый шаблон
      createNewTemplate();
    }
    loadDynamicContainers();
    // Нам важно не менять поведение (инициализация зависит от templateId),
    // поэтому оставляем зависимости как раньше.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const loadTemplate = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/admin/v2/html-templates/${templateId}`);
      setTemplate(response.data);
    } catch (error) {
      console.error('Error loading template:', error);
      setError('Ошибка загрузки шаблона');
    } finally {
      setLoading(false);
    }
  };

  const createNewTemplate = () => {
    setTemplate({
      id: 0,
      name: 'Новый шаблон',
      description: '',
      layout_type: '2-column',
      status: 'draft',
      columns: [
        {
          id: 1,
          column_name: 'main-content',
          column_type: 'main',
          width_value: '70%',
          position: 0,
          content_type: 'static'
        },
        {
          id: 2,
          column_name: 'sidebar',
          column_type: 'sidebar',
          width_value: '30%',
          position: 1,
          content_type: 'static'
        }
      ],
      containers: [],
      metadata: {}
    });
  };

  const loadDynamicContainers = async () => {
    try {
      console.log('🔄 Загружаем свободные экземпляры контейнеров...');
      
      // Загружаем свободные экземпляры контейнеров (без привязки к шаблону)
      const response = await axios.get('/api/admin/containers?free_only=true');
      
      const freeContainers = response.data;
      console.log('📦 Загружено свободных контейнеров:', freeContainers.length);
      setDynamicContainers(freeContainers);
    } catch (error) {
      console.error('❌ Error loading dynamic containers:', error);
    }
  };

  // Обработка размещения в колонках
  const handleDrop = useCallback((item: DragDropItem, columnId: number) => {
    if (!template) return;
    
    // Проверяем, является ли это экземпляром из библиотеки
    const isDynamicContainer = item.id && dynamicContainers.find(dc => dc.id === item.id);
    
    if (isDynamicContainer) {
      // Используем существующий экземпляр
      const newContainer: TemplateContainerRow = {
        ...(item as unknown as TemplateContainerRow),
        position: template.containers.filter(c => c.column_id === columnId).length,
        column_id: columnId,
        placement_type: 'column'
      };
      
      setTemplate({
        ...template,
        containers: [...template.containers, newContainer]
      });
    } else {
      // Для системных контейнеров создаем временный объект
      const newContainer: TemplateContainerRow = {
        id: Date.now(), // временный ID
        container_type: item.dragType === ItemTypes.DYNAMIC_CONTAINER ? 'dynamic' : (item.type as string),
        name: item.name,
        description: item.description,
        position: template.containers.filter(c => c.column_id === columnId).length,
        column_id: columnId,
        settings: {},
        visual_style: item.visual_style || {},
        max_components: item.max_components,
        min_components: item.min_components,
        placement_type: item.placement
      };
      
      setTemplate({
        ...template,
        containers: [...template.containers, newContainer]
      });
    }
    
    // Показываем сообщение об успешном добавлении
    console.log(`✅ Контейнер "${item.name}" добавлен в колонку`);
  }, [template, dynamicContainers]);

  // Обработка размещения вне колонок (header/footer/menu)
  const handleDropOutsideColumns = useCallback(async (item: DragDropItem, placement: OutsidePlacement) => {
    console.log('🎯 handleDropOutsideColumns вызван:', { item, placement });
    
    if (!template) {
      console.log('❌ Нет шаблона');
      return;
    }
    
    // Обрабатываем экземпляры контейнеров из библиотеки слева
    const isDynamicContainer = item.id && dynamicContainers.find(dc => dc.id === item.id);
    console.log('🔍 Проверка динамического контейнера:', { 
      itemId: item.id, 
      isDynamicContainer: !!isDynamicContainer,
      dynamicContainersCount: dynamicContainers.length 
    });
    
    if (isDynamicContainer) {
      // Добавляем существующий экземпляр контейнера в шаблон
      const newContainer: TemplateContainerRow = {
        ...(item as unknown as TemplateContainerRow),
        position: placement === 'top' ? -1000 : 1000,
        placement_type: placement === 'top' ? 'header' : 'footer',
        column_id: null,
        settings: { placement }
      };
      
      console.log('📦 Добавляем контейнер:', newContainer);
      
      // Обновляем состояние шаблона
      setTemplate({
        ...template,
        containers: [...template.containers, newContainer]
      });
      
      console.log(`✅ Контейнер "${item.name}" добавлен ${placement === 'top' ? 'в хедер' : 'в футер'}`);
      return;
    }
    
    console.log('⚠️ Контейнер не найден в динамических контейнерах');
    
    // Проверяем ограничения для системных контейнеров
    if (item.maxCount === 1) {
      const existingCount = template.containers.filter(c => 
        c.container_type === item.type && !c.parent_container_id
      ).length;
      
      if (existingCount >= 1) {
        alert(`Контейнер "${item.name}" может быть добавлен только один раз`);
        return;
      }
    }
    
    const newContainer: TemplateContainerRow = {
      id: Date.now(), // временный ID
      container_type: item.type as string,
      name: item.name,
      description: item.description,
      position: placement === 'top' ? -1000 + template.containers.filter(c => !c.parent_container_id && c.position < 0).length : 
                1000 + template.containers.filter(c => !c.parent_container_id && c.position > 999).length,
      parent_container_id: undefined, // null для контейнеров вне колонок
      settings: { placement },
      visual_style: item.visual_style || {},
      max_components: item.max_components,
      min_components: item.min_components,
      placement_type: placement
    };
    
    setTemplate({
      ...template,
      containers: [...template.containers, newContainer]
    });
    
    // Показываем сообщение об успешном добавлении
    console.log(`✅ Контейнер "${item.name}" добавлен ${placement === 'top' ? 'в верхнюю область' : 'в нижнюю область'}`);
  }, [template, dynamicContainers]);

  const handleRemoveContainer = (containerId: number) => {
    if (!template) return;
    
    setTemplate({
      ...template,
      containers: template.containers.filter(c => c.id !== containerId)
    });
  };

  const handleUpdateContainers = (updatedContainers: TemplateContainerRow[]) => {
    if (!template) return;
    
    setTemplate({
      ...template,
      containers: updatedContainers
    });
  };

  const handleUpdateColumn = (updatedColumn: TemplateColumn) => {
    if (!template) return;
    
    const updatedColumns = template.columns.map(col => 
      col.id === updatedColumn.id ? updatedColumn : col
    );
    
    setTemplate({
      ...template,
      columns: updatedColumns
    });
    
    console.log(`⚙️ Обновлена ширина колонки "${updatedColumn.column_name}": ${updatedColumn.width_value}`);
  };

  // Создание динамического контейнера
  const handleCreateDynamicContainer = async () => {
    if (!template || !newDynamicName.trim()) {
      setError('Название контейнера обязательно');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      console.log('🔄 Создаем контейнер через новый API...');
      
      // Создаем через новый API
      const response = await axios.post('/api/admin/containers', {
        name: newDynamicName.trim(),
        description: newDynamicDescription.trim(),
        container_type: 'dynamic',
        placement_rules: newDynamicPlacement,
        min_components: 1,
        max_components: 5,
        is_system: false,
        is_multiple: true,
        sort_order: 100
      });
      
      const newContainer = response.data;
      console.log('✨ Создан динамический контейнер через новый API:', newContainer);
      
      // Обновляем список динамических контейнеров
      setDynamicContainers([...dynamicContainers, newContainer]);
      
      // Очищаем форму
      setNewDynamicName('');
      setNewDynamicDescription('');
      setNewDynamicPlacement('any');
      setShowCreateDynamic(false);
    } catch (error: unknown) {
      // Fallback: пробуем старый API
      console.log('⚠️ Новый API недоступен, пробуем старый...');
      try {
        const fallbackResponse = await axios.post('/api/admin/v2/dynamic-containers', {
          name: newDynamicName.trim(),
          description: newDynamicDescription.trim(),
          placement: newDynamicPlacement,
          template_id: template.id
        });
        
        const newContainer = fallbackResponse.data;
        console.log('✨ Создан динамический контейнер через старый API:', newContainer);
        
        setDynamicContainers([...dynamicContainers, newContainer]);
        setNewDynamicName('');
        setNewDynamicDescription('');
        setNewDynamicPlacement('any');
        setShowCreateDynamic(false);
      } catch (fallbackError: unknown) {
        console.error('Error creating dynamic container:', fallbackError);
        setError(getApiErrorMessage(fallbackError, 'Ошибка при создании контейнера'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Обработка изменения типа макета
  const handleLayoutTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!template) return;
    
    const newLayoutType = e.target.value;
    let newColumns: TemplateColumn[] = [];
    
    // Создаем колонки в зависимости от типа макета
    switch (newLayoutType) {
      case '1-column':
        newColumns = [
          {
            id: Date.now(),
            column_name: 'main-content',
            column_type: 'main',
            width_value: '100%',
            position: 0,
            content_type: 'static'
          }
        ];
        break;
      case '2-column':
        newColumns = [
          {
            id: Date.now(),
            column_name: 'main-content',
            column_type: 'main',
            width_value: '70%',
            position: 0,
            content_type: 'static'
          },
          {
            id: Date.now() + 1,
            column_name: 'sidebar',
            column_type: 'sidebar',
            width_value: '30%',
            position: 1,
            content_type: 'static'
          }
        ];
        break;
      case '3-column':
        newColumns = [
          {
            id: Date.now(),
            column_name: 'left-sidebar',
            column_type: 'sidebar',
            width_value: '20%',
            position: 0,
            content_type: 'static'
          },
          {
            id: Date.now() + 1,
            column_name: 'main-content',
            column_type: 'main',
            width_value: '60%',
            position: 1,
            content_type: 'static'
          },
          {
            id: Date.now() + 2,
            column_name: 'right-sidebar',
            column_type: 'sidebar',
            width_value: '20%',
            position: 2,
            content_type: 'static'
          }
        ];
        break;
    }
    
    // Обновляем шаблон с новыми колонками
    setTemplate({
      ...template,
      layout_type: newLayoutType,
      columns: newColumns,
      // Очищаем контейнеры в колонках (оставляем только вне колонок)
      containers: template.containers.filter(c => !c.column_id)
    });
    
    console.log(`⚙️ Макет изменен на: ${newLayoutType}`);
  };

  const handleEditContainer = (container: TemplateContainerRow) => {
    setEditingContainer(container);
    setShowContainerEditor(true);
  };

  const handleContainerSaved = (updatedContainer: TemplateContainerRow) => {
    if (!template) return;
    
    // Обновляем контейнер в шаблоне
    const updatedContainers = template.containers.map(c =>
      c.id === updatedContainer.id ? updatedContainer : c
    );
    
    setTemplate({
      ...template,
      containers: updatedContainers
    });
    
    console.log('✅ Контейнер обновлен:', updatedContainer.name);
  };

  const handleContainerCreated = (newContainer: TemplateContainerRow) => {
    if (!template) return;
    
    // Добавляем новый контейнер к шаблону
    setTemplate({
      ...template,
      containers: [...template.containers, newContainer]
    });
    
    console.log('✅ Создан новый контейнер:', newContainer.name);
  };

  const handleCloseContainerEditor = () => {
    setShowContainerEditor(false);
    setEditingContainer(null);
  };

  const handleCloseContainerCreation = () => {
    setShowContainerCreation(false);
  };

  const handleShowContainerCreation = () => {
    setShowContainerCreation(true);
  };

  const handleSaveTemplate = async () => {
    if (!template) return;
    
    setLoading(true);
    setError(''); // Очищаем предыдущие ошибки
    
    try {
      const method = template.id ? 'PUT' : 'POST';
      const url = template.id 
        ? `/api/admin/v2/html-templates/${template.id}`
        : '/api/admin/v2/html-templates';
      
      // Подготавливаем данные для отправки
      const payload: HtmlTemplateSavePayload = {
        name: template.name || 'Новый шаблон',
        description: template.description || '',
        layout_type: template.layout_type || '1-column',
        metadata: template.metadata || {},
        status: template.status || 'draft',
        // Добавляем колонки и контейнеры
        columns: template.columns || [],
        containers: template.containers || [],
        // Прозрачность компонентов (если задана)
        component_opacity: template.component_opacity ?? null
      };
      
      // Для обновления добавляем ID
      if (template.id) {
        payload.id = template.id;
      }
      
      console.log('📊 Отправляем колонки:', payload.columns.length);
      console.log('📦 Отправляем контейнеры:', payload.containers.length);
      
      console.log('📤 Отправляем шаблон:', payload);
      
      const response = await axios({
        method,
        url,
        data: payload
      });
      
      const savedTemplate = response.data;
      console.log('✅ Шаблон успешно сохранен:', savedTemplate);
      
      setTemplate(savedTemplate);
      if (onSave) {
        onSave(savedTemplate);
      }
      
      // Валидируем шаблон после сохранения
      if (savedTemplate.id) {
        validateTemplate(savedTemplate.id);
      }
      
      // Показываем успешное сообщение
      alert(`✅ Шаблон "${savedTemplate.name}" сохранен!`);
    } catch (error: unknown) {
      console.error('❌ Ошибка при сохранении:', error);
      setError(getApiErrorMessage(error, 'Ошибка сохранения шаблона'));
      alert('❌ Ошибка сохранения! Проверьте консоль.');
    } finally {
      setLoading(false);
    }
  };

  const validateTemplate = async (id: number) => {
    try {
      const response = await axios.post(`/api/admin/v2/html-templates/${id}/validate`);
      setValidationResults(response.data as TemplateValidationResults);
    } catch (error) {
      console.error('Error validating template:', error);
    }
  };

  if (loading) {
    return <div className="template-builder__loading">Загрузка...</div>;
  }

  if (!template) {
    return <div className="template-builder__error">Шаблон не загружен</div>;
  }

  return (
    <>
      <DndProvider backend={HTML5Backend}>
      <div className="template-builder">
        {/* Заголовок */}
        <div className="template-builder__header">
          <div className="template-builder__header-info">
            <input
              type="text"
              className="u2-input template-builder__name-input"
              value={template.name}
              onChange={(e) => setTemplate({ ...template, name: e.target.value })}
              placeholder="Название шаблона"
            />
            <textarea
              className="u2-textarea template-builder__description-input"
              value={template.description}
              onChange={(e) => setTemplate({ ...template, description: e.target.value })}
              placeholder="Описание шаблона"
              rows={2}
            />
            
            {/* Прозрачность компонентов */}
            <div className="template-builder__opacity-field">
              <label className="u2-label">
                Прозрачность компонентов
                <span className="u2-hint" title="Если не задано, используется глобальная настройка (50%)">ⓘ</span>
              </label>
              <div className="template-builder__opacity-row">
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={template.component_opacity !== null && template.component_opacity !== undefined 
                    ? Math.round(template.component_opacity * 100) 
                    : 50}
                  onChange={(e) => setTemplate({ 
                    ...template, 
                    component_opacity: parseInt(e.target.value) / 100 
                  })}
                  style={{ flex: 1 }}
                />
                <span className="template-builder__opacity-value">
                  {template.component_opacity !== null && template.component_opacity !== undefined 
                    ? `${Math.round(template.component_opacity * 100)}%` 
                    : 'Авто'}
                </span>
                {template.component_opacity !== null && template.component_opacity !== undefined && (
                  <button
                    type="button"
                    className="u2-btn is-ghost is-sm"
                    onClick={() => setTemplate({ ...template, component_opacity: null })}
                    title="Сбросить на авто"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>
          
          <div className="template-builder__header-actions">
            <button
              type="button"
              className="u2-btn is-primary"
              onClick={handleSaveTemplate}
              disabled={loading}
            >
              Сохранить
            </button>
            {onCancel && (
              <button
                type="button"
                className="u2-btn is-secondary"
                onClick={onCancel}
              >
                Отмена
              </button>
            )}
          </div>
        </div>

        {/* Ошибки и валидация */}
        {error && (
          <div className="template-builder__error-message">
            {error}
          </div>
        )}
        
        {validationResults && (
          <div className="template-builder__validation">
            {validationResults.error_count > 0 && (
              <div className="validation-errors">
                ⚠️ Ошибок: {validationResults.error_count}
              </div>
            )}
            {validationResults.warning_count > 0 && (
              <div className="validation-warnings">
                ⚡ Предупреждений: {validationResults.warning_count}
              </div>
            )}
          </div>
        )}

        <div className="template-builder__main">
          {/* Левая панель - библиотека контейнеров */}
          <div className="template-builder__library">
            <h3>Библиотека контейнеров</h3>
            
            {/* Список контейнеров */}
            <div className="template-builder__library-list">
              {/* Показываем только динамические контейнеры из библиотеки */}
              {false && (
                <>
                  <button
                    type="button"
                    className="template-builder__create-container-btn"
                    onClick={handleShowContainerCreation}
                  >
                    ➕ Создать контейнер из шаблона
                  </button>
                  
                  {SYSTEM_CONTAINERS.map((container, index) => (
                    <DraggableContainer
                      key={index}
                      container={container}
                      type={ItemTypes.CONTAINER}
                    />
                  ))}
                </>
              )}
              
              {true && (
                <>
                  <button
                    type="button"
                    className="u2-btn is-primary template-builder__create-dynamic-btn"
                    onClick={() => setShowCreateDynamic(true)}
                  >
                    Создать динамический контейнер
                  </button>
                  
                  {/* Форма создания динамического контейнера */}
                  {showCreateDynamic && (
                    <div className="template-builder__create-dynamic-form">
                      <h4>Новый динамический контейнер</h4>
                      <div className="form-group">
                        <label>Название:</label>
                        <input
                          type="text"
                          value={newDynamicName}
                          onChange={(e) => setNewDynamicName(e.target.value)}
                          placeholder="Например: Боковая колонка для новостей"
                          className="u2-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>Описание:</label>
                        <textarea
                          value={newDynamicDescription}
                          onChange={(e) => setNewDynamicDescription(e.target.value)}
                          placeholder="Описание назначения контейнера"
                          className="u2-textarea"
                          rows={2}
                        />
                      </div>
                      <div className="form-group">
                        <label>Размещение:</label>
                        <select 
                          value={newDynamicPlacement}
                          onChange={(e) => setNewDynamicPlacement(e.target.value as DynamicContainer['placement'])}
                          className="u2-select"
                        >
                          <option value="any">Любое место</option>
                          <option value="main-only">Только основная колонка</option>
                          <option value="sidebar-only">Только боковые колонки</option>
                          <option value="top-bottom">Верх или низ</option>
                        </select>
                      </div>
                      <div className="form-actions">
                        <button
                          type="button"
                          className="u2-btn is-primary"
                          onClick={handleCreateDynamicContainer}
                          disabled={loading || !newDynamicName.trim()}
                        >
                          {loading ? 'Создаем...' : 'Создать'}
                        </button>
                        <button
                          type="button"
                          className="u2-btn is-secondary"
                          onClick={() => {
                            setShowCreateDynamic(false);
                            setNewDynamicName('');
                            setNewDynamicDescription('');
                            setNewDynamicPlacement('any');
                            setError('');
                          }}
                          disabled={loading}
                        >
                          Отмена
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {dynamicContainers.map(container => (
                    <DraggableContainer
                      key={container.id}
                      container={container}
                      type={ItemTypes.DYNAMIC_CONTAINER}
                    />
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Центральная область - canvas */}
          <div className="template-builder__canvas">
            <h3>Структура шаблона</h3>
            
            <div className="template-builder__layout-selector">
              <label>Тип макета:</label>
              <select
                className="u2-select"
                value={template.layout_type}
                onChange={handleLayoutTypeChange}
                disabled={template.id > 0} // Нельзя менять тип у существующего шаблона
              >
                <option value="1-column">1 колонка</option>
                <option value="2-column">2 колонки</option>
                <option value="3-column">3 колонки</option>
              </select>
              {template.id === 0 && (
                <div className="template-builder__layout-info">
                  ⚠️ Изменение типа макета пересоздаст колонки
                </div>
              )}
            </div>
            
            {/* Верхняя область для header/menu */}
            <OutsideDropZone 
              placement="top"
              containers={template.containers}
              onDrop={handleDropOutsideColumns}
              onRemoveContainer={handleRemoveContainer}
              onUpdateContainers={handleUpdateContainers}
            />
            
            {/* Колонки с контентом */}
            <div className="template-builder__columns">
              {template.columns
                .sort((a, b) => a.position - b.position)
                .map(column => (
                  <TemplateColumnComponent
                    key={column.id}
                    column={column}
                    containers={template.containers}
                    onDrop={handleDrop}
                    onRemoveContainer={handleRemoveContainer}
                    onEditContainer={handleEditContainer}
                    onUpdateContainers={handleUpdateContainers}
                    onUpdateColumn={handleUpdateColumn}
                  />
                ))}
            </div>
            
            {/* Нижняя область для footer/menu */}
            <OutsideDropZone 
              placement="bottom"
              containers={template.containers}
              onDrop={handleDropOutsideColumns}
              onRemoveContainer={handleRemoveContainer}
              onUpdateContainers={handleUpdateContainers}
            />
          </div>

          {/* Правая панель - настройки */}
          <div className="template-builder__settings">
            <h3>Настройки</h3>
            
            <div className="template-builder__info">
              <p>Нажмите ✏️ рядом с контейнером для редактирования</p>
                
                <div className="template-builder__stats">
                  <h4>Статистика шаблона:</h4>
                  <ul>
                    <li>Колонок: {template.columns.length}</li>
                    <li>Контейнеров: {template.containers.length}</li>
                    <li>Статус: {template.status}</li>
                  </ul>
                </div>
                
                <div className="template-builder__tips">
                  <h4>Подсказки:</h4>
                  <ul>
                    <li>🎯 Перетаскивайте контейнеры из библиотеки в колонки</li>
                    <li>🔴 Красная подсветка = нельзя разместить</li>
                    <li>🟢 Зеленая подсветка = можно разместить</li>
                    <li>📏 Колонки автоматически подстраиваются под контент</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
    </DndProvider>

    {/* Модальные окна */}
    {showContainerEditor && editingContainer?.id && (
      <ContainerEditor
        containerId={editingContainer.id!}
        onSave={handleContainerSaved}
        onClose={handleCloseContainerEditor}
      />
    )}

    {showContainerCreation && template?.id && (
      <ContainerCreationModal
        templateId={template.id!}
        onCreateContainer={handleContainerCreated}
        onClose={handleCloseContainerCreation}
      />
    )}
    </>
  );
};

export default HtmlTemplateBuilder;
